import * as React from "react";
import { graphql, useFragment } from "react-relay";

import { getTimezoneOffset } from "date-fns-tz";

import Selector, { type ActionMeta, type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import { condensedStyles } from "~/ui/selectors/SelectorUtils";

import type { TimezoneSelector_viewer$key } from "~/__generated__/TimezoneSelector_viewer.graphql";

const { useMemo, useCallback } = React;

// TODO: As of 2023-06-14, this setting refers to server timezone only, so using client timezone as the hint
// here is inaccurate. It should use the GraphQL value, accounting for automatic values from e.g. Google
// UPDATE 2023-09-28: This is now also used for the timezone that calendars are displayed in. This probably
//	complicates the problem mentioned above because we _want_ to use the client timezone for calendars.
const systemTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

const automaticTimezoneOption = {
	label: `Automatic (${systemTimezoneName})`,
	value: "",
	utcOffset: getTimezoneOffset(systemTimezoneName),
};

export const getTimezoneInfo = (timezone: string) => {
	const utcOffset = getTimezoneOffset(timezone) / 1000 / 60 / 60;

	if (timezone === "" || isNaN(utcOffset)) {
		return {
			name: automaticTimezoneOption.value,
			description: automaticTimezoneOption.label,
		};
	}

	const hour = String(Math.floor(Math.abs(utcOffset))).padStart(2, "0");
	const minute = String(utcOffset % 1).padStart(2, "0");

	return {
		name: timezone,
		description: `(UTC${utcOffset >= 0 ? "+" : "-"}${hour}${minute}) ${timezone}`,
		utcOffset,
	};
};

export default function TimezoneSelector({
	viewer,
	value,
	onChange,
}: {
	viewer: TimezoneSelector_viewer$key;
	value: string;
	onChange: (value: string) => void;
}) {
	const { supportedTimezones } = useFragment(
		graphql`
			fragment TimezoneSelector_viewer on Viewer {
				supportedTimezones {
					description
					name
				}
			}
		`,
		viewer,
	);
	const timezoneOptions = useMemo(() => {
		const options = supportedTimezones.map(zone => ({
			label: zone.description,
			value: zone.name,
		}));

		options.unshift(automaticTimezoneOption);
		return options;
	}, [supportedTimezones]);

	const changeTimezoneSetting = useCallback(
		(timezone: SingleValue<SelectorOption>, actionMeta: ActionMeta<SelectorOption>) => {
			const selectedTimezone =
				!timezone || actionMeta.action === "clear" || typeof timezone.value !== "string"
					? automaticTimezoneOption.value
					: timezone.value;

			onChange(selectedTimezone);
		},
		[onChange],
	);

	return (
		<Selector
			isSearchable
			condensed
			width="275px"
			isClearable={value !== automaticTimezoneOption.value}
			data-test-id="preferred-timezone"
			placeholder="Automatic"
			menuPlacement="auto"
			menuPortalTarget={document.body}
			value={timezoneOptions.find(option => option.value === value)}
			options={timezoneOptions}
			onChange={changeTimezoneSetting}
			styles={{
				menu: (provided, props) => ({
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					...(condensedStyles.menu ? condensedStyles.menu(provided, props as any) : provided),
					right: 0,
				}),
			}}
		/>
	);
}
